import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedUserType = localStorage.getItem('userType');
    const isImpersonating = localStorage.getItem('impersonation_active') === 'true';

    if (token && savedUser) {
      const parsed = JSON.parse(savedUser);
      setUser(parsed);
      setUserType(savedUserType);
      setImpersonating(isImpersonating);

      // A session that logged in BEFORE a feature flag went live keeps a
      // stale `user` in localStorage, so flag-gated UI (e.g. Buyer
      // Notifications) stays hidden until a manual logout/login. Refresh the
      // merchant profile once on mount and merge the backend-authoritative
      // feature_flags in. Fails closed: on any error the stored user is kept.
      if (savedUserType === 'merchant') {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        fetch(`${backendUrl}/api/auth/merchant/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            const flags = data?.feature_flags;
            if (!flags) return;
            const merged = { ...parsed, feature_flags: flags };
            localStorage.setItem('user', JSON.stringify(merged));
            setUser(merged);
          })
          .catch(() => {});
      }
    }
    setLoading(false);
  }, []);

  const login = (token, userData, type, refreshToken) => {
    localStorage.removeItem('staffToken');
    localStorage.removeItem('staffUser');
    localStorage.setItem('token', token);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userType', type);
    setUser(userData);
    setUserType(type);
  };

  const updateUser = (updatedUserData) => {
    localStorage.setItem('user', JSON.stringify(updatedUserData));
    setUser(updatedUserData);
  };

  const logout = () => {
    const rt = localStorage.getItem('refreshToken');
    if (rt) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${backendUrl}/api/auth/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      }).catch(() => {});
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    localStorage.removeItem('impersonation_active');
    localStorage.removeItem('admin_backup_token');
    localStorage.removeItem('admin_backup_user');
    localStorage.removeItem('admin_backup_userType');
    localStorage.removeItem('admin_backup_refreshToken');
    setUser(null);
    setUserType(null);
    setImpersonating(false);
  };

  const startImpersonation = (merchantToken, merchantData) => {
    // Backup current admin session
    localStorage.setItem('admin_backup_token', localStorage.getItem('token'));
    localStorage.setItem('admin_backup_user', localStorage.getItem('user'));
    localStorage.setItem('admin_backup_userType', localStorage.getItem('userType'));
    const rt = localStorage.getItem('refreshToken');
    if (rt) localStorage.setItem('admin_backup_refreshToken', rt);

    // Set merchant session
    localStorage.setItem('token', merchantToken);
    localStorage.setItem('user', JSON.stringify(merchantData));
    localStorage.setItem('userType', 'merchant');
    localStorage.setItem('impersonation_active', 'true');

    setUser(merchantData);
    setUserType('merchant');
    setImpersonating(true);
  };

  const endImpersonation = () => {
    // Restore admin session
    const adminToken = localStorage.getItem('admin_backup_token');
    const adminUser = localStorage.getItem('admin_backup_user');
    const adminType = localStorage.getItem('admin_backup_userType');
    const adminRefresh = localStorage.getItem('admin_backup_refreshToken');

    if (adminToken && adminUser) {
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', adminUser);
      localStorage.setItem('userType', adminType || 'admin');
      if (adminRefresh) localStorage.setItem('refreshToken', adminRefresh);

      setUser(JSON.parse(adminUser));
      setUserType(adminType || 'admin');
    }

    // Clean up impersonation state
    localStorage.removeItem('impersonation_active');
    localStorage.removeItem('admin_backup_token');
    localStorage.removeItem('admin_backup_user');
    localStorage.removeItem('admin_backup_userType');
    localStorage.removeItem('admin_backup_refreshToken');
    setImpersonating(false);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return (user.permissions || []).includes(permission);
  };

  const hasAnyPermission = (...permissions) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return permissions.some(p => (user.permissions || []).includes(p));
  };

  return (
    <AuthContext.Provider value={{
      user, userType, loading, impersonating,
      login, updateUser, logout,
      startImpersonation, endImpersonation,
      hasPermission, hasAnyPermission,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
